import styles from './CasesFirstView.module.scss'
import { useEffect, useRef, useState } from 'react'
import { gsap, Power4 } from 'gsap'
import Breadcrumb from 'components/commons/Breadcrumb/Breadcrumb'
import CaseFirstViewItem from '../CaseFirstViewItem'

const tweenVars: gsap.TimelineVars = [
  {
    y: '50',
    duration: 1,
    opacity: 0,
    ease: Power4.easeOut,
  },
  {
    x: '-50',
    duration: 0.5,
    opacity: 0,
    ease: Power4.easeOut,
  },
  {
    duration: 1,
    width: '40px',
    border: '1px solid #fff',
    transform: 'scaleY(0.5)',
    transformOrigin: '0 100%',
    ease: Power4.easeOut,
  },
]

export type firstViewItem = {
  breadcrumb: string
  title1: string
  subtitle?: string
  introduction_text: string
  company: string
  tag1text?: string
  tag1src?: string
  tag2text?: string
  tag2src?: string
  tag3text?: string
  tag3src?: string
  color: string
  background: string
  staff1?: string
  staff2?: string
  staff3?: string
  linkText?: string
  linkUrl?: string
  image?: string
  alt?: string
  imgClass?: string
  noProtrude?: boolean
  industryTagText?: string
  industryTagSrc?: string
}

type Props = {
  firstViewItem: firstViewItem
}

const CasesFirstView: React.FC<Props> = ({ firstViewItem }) => {
  const timeline = gsap.timeline()

  const wrapper = useRef<HTMLDivElement>()
  const services_link = useRef<HTMLAnchorElement>()
  const service_name = useRef<HTMLHeadingElement>()
  const border = useRef<HTMLDivElement>()
  const title = useRef<HTMLDivElement>()
  const introduction = useRef<HTMLParagraphElement>()
  const sub_text = useRef<HTMLDivElement>()
  const image1_pc = useRef<HTMLImageElement>()
  const image2_pc = useRef<HTMLImageElement>()
  const image3_pc = useRef<HTMLImageElement>()
  const tag = useRef<HTMLUListElement>()

  const leftRef = useRef<HTMLDivElement>(null)
  const [leftHeight, setLeftHeight] = useState<number>(0)

  useEffect(() => {
    const updateLeftHeight = () => {
      if (window.innerWidth >= 1200 && leftRef.current) {
        setLeftHeight(leftRef.current.offsetHeight + 80)
      } else {
        setLeftHeight(undefined)
      }
    }

    updateLeftHeight()
    window.addEventListener('resize', updateLeftHeight)

    return () => {
      window.removeEventListener('resize', updateLeftHeight)
    }
  }, [])

  const paths = [
    { name: 'TOP', url: '/' },
    { name: 'Case study', url: '/cases' },
    { name: firstViewItem.breadcrumb, url: '#' },
  ]

  const wrapStyle: React.CSSProperties = {
    background: firstViewItem.background,
  }

  const colorStyle: React.CSSProperties = {
    color: firstViewItem.color,
  }

  useEffect(() => {
    timeline
      .to(wrapper.current, { css: { opacity: 1 } }, 0)
      .from(services_link.current, tweenVars[0], 0.2)
      .from(service_name.current, tweenVars[0], 0.4)
      .to(border.current, tweenVars[2], 0.6)
      .from(title.current, tweenVars[0], 0.8)
      .from(introduction.current, tweenVars[0], 1)
      .from(sub_text.current, tweenVars[0], 1.2)
      .from(tag.current, tweenVars[0], 1.4)
      .from(image1_pc.current, tweenVars[0], 1.5)
      .from(image2_pc.current, tweenVars[0], 1.6)
      .from(image3_pc.current, tweenVars[0], 1.8)
  }, [])

  const imgClass = firstViewItem.imgClass ? styles[firstViewItem.imgClass] : ''

  return (
    <section className={styles.wrapper} style={wrapStyle}>
      <div className={styles.fv}>
        <div className={styles.fv_left} ref={leftRef}>
          <div className={styles.breadcrumbs} style={colorStyle}>
            <Breadcrumb paths={paths} />
          </div>
          <CaseFirstViewItem
            subtitle={firstViewItem.subtitle}
            title1={firstViewItem.title1}
            introduction_text={firstViewItem.introduction_text}
            company={firstViewItem.company}
            tag1text={firstViewItem.tag1text}
            tag1src={firstViewItem.tag1src}
            tag2text={firstViewItem.tag2text}
            tag2src={firstViewItem.tag2src}
            tag3text={firstViewItem.tag3text}
            tag3src={firstViewItem.tag3src}
            color={firstViewItem.color}
            staff1={firstViewItem.staff1}
            staff2={firstViewItem.staff2}
            staff3={firstViewItem.staff3}
            linkText={firstViewItem.linkText}
            linkUrl={firstViewItem.linkUrl}
            industryTagText={firstViewItem.industryTagText}
            industryTagSrc={firstViewItem.industryTagSrc}
          />
        </div>
        <div className={`${styles.fv_img} ${imgClass}`} style={{ maxHeight: leftHeight }}>
          <img
            src={firstViewItem.image}
            alt={firstViewItem.alt}
            ref={image3_pc}
            className={firstViewItem.noProtrude ? styles.fv_img_noProtrude : styles.fv_img_protrude}
          />
        </div>
      </div>
    </section>
  )
}

export default CasesFirstView
